import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout';
import { useAuth } from '../context/AuthContext';
import './Reports.css';

const REPORT_CARDS = [
  { key: 'inventory', title: 'Inventory Reports', description: 'Parts received and put away by bin location.', icon: '📦', link: '/inventory' },
  { key: 'dispatch', title: 'Dispatch Reports', description: 'Dispatched orders with signed delivery notes.', icon: '🚚', link: '/delivery' },
  { key: 'cycleCount', title: 'Cycle Count Reports', description: 'Stock count records and variances.', icon: '🔢', link: '/reports/cycle-count' },
  { key: 'variance', title: 'Variance Reports', description: 'Receiving and putaway quantity mismatches (expected vs entered).', icon: '📉', link: '/reports/variance' },
  { key: 'orderManagement', title: 'Order Management Reports', description: 'Picking sessions and order fulfillment.', icon: '📋', link: '/reports/order-picking' },
  {
    key: 'failedPickLines',
    title: 'Failed pick lines',
    description: 'Short picks and not-available lines (dispatch blocked until resolved).',
    icon: '⚠️',
    link: '/reports/failed-picks',
    adminOnlyLink: true,
  },
  { key: 'returns', title: 'Returns & Recall Reports', description: 'Returns transactions and recall items sent to main branch.', icon: '↩️', link: '/reports/returns' },
  { key: 'recallReport', title: 'Recall Report', description: 'Recall list from import and fulfillment (expected vs fulfilled qty).', icon: '↩️', link: '/reports/recall' },
  { key: 'qc', title: 'Quality Control Reports', description: 'QC pass/fail from receiving and quarantine.', icon: '✅', link: '/reports/qc' },
  { key: 'stock', title: 'Stock Reports', description: 'Summary of parts and total quantity on hand.', icon: '📊', link: '/reports/stock' },
];

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const Reports = () => {
  const navigate = useNavigate();
  const { fetchWithAuth, user, isAdmin } = useAuth();
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');

  const loadLocations = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await fetchWithAuth('/api/consignment-locations');
      if (res.ok) {
        const list = await res.json();
        setLocations(Array.isArray(list) ? list : []);
      }
    } catch {
      setLocations([]);
    }
  }, [fetchWithAuth, isAdmin]);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  const loadSummary = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      let url = '/api/reports/summary';
      if (isAdmin && selectedLocationId !== '') {
        url += `?consignment_location_id=${encodeURIComponent(selectedLocationId)}`;
      }
      const response = await fetchWithAuth(url);
      if (!response.ok) throw new Error('Failed to load report summary');
      const data = await response.json();
      setSummary(data);
    } catch (err) {
      setError(err.message || 'Failed to load reports');
      setSummary(null);
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth, isAdmin, selectedLocationId]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const getStats = (key) => {
    if (!summary) return { count: 0, lastAt: null, extra: null };
    switch (key) {
      case 'inventory':
        return { count: summary.inventory?.count ?? 0, lastAt: summary.inventory?.lastAt ?? null, extra: summary.inventory?.totalQuantity };
      case 'dispatch':
        return { count: summary.dispatch?.count ?? 0, lastAt: summary.dispatch?.lastAt ?? null };
      case 'cycleCount':
        return { count: summary.cycleCount?.count ?? 0, lastAt: summary.cycleCount?.lastAt ?? null };
      case 'orderManagement':
        return { count: summary.orderManagement?.count ?? 0, lastAt: summary.orderManagement?.lastAt ?? null };
      case 'returns':
        return { count: summary.returns?.count ?? 0, lastAt: summary.returns?.lastAt ?? null };
      case 'qc':
        return {
          count: summary.qc?.count ?? 0,
          lastAt: summary.qc?.lastAt ?? null,
          extra: summary.qc ? `Pass: ${summary.qc.passCount ?? 0} / Fail: ${summary.qc.failCount ?? 0}` : null,
        };
      case 'stock':
        return {
          count: summary.stock?.partCount ?? 0,
          lastAt: summary.stock?.lastAt ?? null,
          extra: summary.stock?.totalQuantity != null ? `Total qty: ${summary.stock.totalQuantity}` : null,
        };
      case 'variance':
        return {
          count: summary.variance?.count ?? 0,
          lastAt: summary.variance?.lastAt ?? null,
          extra: null,
        };
      case 'failedPickLines':
        return {
          count: summary.failedPickLines?.count ?? 0,
          lastAt: summary.failedPickLines?.lastAt ?? null,
          extra: null,
        };
      case 'recallReport':
        return {
          count: summary.recall?.count ?? 0,
          lastAt: summary.recall?.lastAt ?? null,
          extra: null,
        };
      default:
        return { count: 0, lastAt: null, extra: null };
    }
  };

  const recallCount = summary?.recall?.count ?? 0;

  return (
    <Layout>
      <div className="reports-container">
        <div className="reports-header">
          <h1>Reports</h1>
          <div className="reports-header-actions">
            {isAdmin && (
              <label className="reports-location-filter">
                <span className="reports-location-label">Consignment location:</span>
                <select
                  value={selectedLocationId}
                  onChange={(e) => setSelectedLocationId(e.target.value)}
                  className="reports-location-select"
                >
                  <option value="">All locations</option>
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name || loc.code || `Location #${loc.id}`}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {!isAdmin && user?.consignmentLocationName && (
              <span className="reports-location-badge">Location: {user.consignmentLocationName}</span>
            )}
            <button type="button" className="reports-refresh" onClick={loadSummary}>
              🔄 Refresh
            </button>
          </div>
        </div>
        <p className="reports-subtitle">
          {isAdmin
            ? 'Summarized view of inventory, dispatch, cycle count, orders, returns, recall, QC, and stock. Filter by consignment location or view all.'
            : 'Summarized view for your assigned location. Data is synced from ChesiWMS-Lite.'}
        </p>
        {error && <div className="reports-error">{error}</div>}
        {isLoading ? (
          <div className="reports-loading">Loading...</div>
        ) : (
          <div className="reports-grid">
            {REPORT_CARDS.map((card) => {
              const stats = getStats(card.key);
              const displayCount = card.key === 'returns' ? (stats.count + recallCount) : stats.count;
              return (
                <div key={card.key} className="reports-card">
                  <div className="reports-card-header">
                    <span className="reports-card-icon">{card.icon}</span>
                    <h2 className="reports-card-title">{card.title}</h2>
                  </div>
                  <p className="reports-card-desc">{card.description}</p>
                  <div className="reports-card-stats">
                    <span className="reports-card-count">Records: {displayCount}</span>
                    {stats.extra != null && <span className="reports-card-extra">{stats.extra}</span>}
                    <span className="reports-card-date">Last: {formatDate(stats.lastAt)}</span>
                  </div>
                  {card.link && (!card.adminOnlyLink || isAdmin) && (
                    <button type="button" className="reports-card-link" onClick={() => navigate(card.link)}>
                      View details →
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Reports;
